import { useEffect, useState, type FC } from "react";
import { message } from "antd";

import { Modal } from "../../../shared";
import ContactInformation from "../../NewAffiliate/ContactInformation/ContactInformation";
import {
  type ContactInformationFormValues,
} from "../../NewAffiliate/ContactInformation/contactInformationData";
import { mapContactToPatchPayload } from "../../../../queries/affiliates/affiliateMappers";
import { usePatchAffiliate } from "../../../../queries/affiliates/usePatchAffiliate";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import type { OperatorAffiliateDetail } from "../../../../types/affiliates";

// Returned when the operator hits Save without modifying anything — we
// surface it as a friendly info toast instead of a red error.
const NO_CHANGES_ERROR_CODE = "AFFILIATE_NO_CHANGES_DETECTED";

export type EditAffiliateInfoModalProps = {
  open: boolean;
  affiliate: OperatorAffiliateDetail;
  onClose: () => void;
};

const detailToFormValues = (
  detail: OperatorAffiliateDetail,
): Partial<ContactInformationFormValues> => ({
  name: detail.contact_name || detail.company_name || "",
  email: detail.email ?? "",
  phone: detail.phone ?? "",
  websiteUrl: detail.website_url ?? "",
  addressLine1: detail.address?.line1 ?? "",
  addressLine2: detail.address?.line2 ?? "",
  city: detail.address?.city ?? "",
  state: detail.address?.state ?? undefined,
  zipCode: detail.address?.zip ?? "",
});

const EditAffiliateInfoModal: FC<EditAffiliateInfoModalProps> = ({
  open,
  affiliate,
  onClose,
}) => {
  const [values, setValues] = useState<Partial<ContactInformationFormValues>>(
    detailToFormValues(affiliate),
  );
  const patch = usePatchAffiliate();

  useEffect(() => {
    if (open) setValues(detailToFormValues(affiliate));
  }, [open, affiliate]);

  const handleSubmit = async () => {
    try {
      const updated = await patch.mutateAsync({
        affiliateId: affiliate.affiliate_id,
        payload: mapContactToPatchPayload(values),
      });
      message.success(
        getApiSuccessMessage(updated, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED),
      );
      onClose();
    } catch (err) {
      const envelope = (
        err as {
          response?: { data?: { error_code?: string; message?: string } };
        }
      )?.response?.data;
      if (envelope?.error_code === NO_CHANGES_ERROR_CODE) {
        message.info(AppStrings.EDIT_AFFILIATE_NO_CHANGES);
        onClose();
        return;
      }
      message.error(getApiErrorMessage(err, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={AppStrings.PAM_AFFILIATE_EDIT_INFO_MODAL_TITLE}
      size="lg"
      destroyOnHidden
      data-testid="edit-affiliate-info-modal"
      primaryAction={{
        label: AppStrings.SAVE,
        onClick: handleSubmit,
        loading: patch.isPending,
        disabled: patch.isPending,
      }}
      secondaryAction={{
        label: AppStrings.CANCEL,
        onClick: onClose,
      }}
    >
      {/* Modal footer owns Save/Cancel; hide email + Portal access
          because the PATCH endpoint won't accept those fields. */}
      <ContactInformation
        initialValues={values}
        onValuesChange={setValues}
        hideFooter
        hideImmutableFields
      />
    </Modal>
  );
};

export default EditAffiliateInfoModal;
